import React, { useState, useEffect, createContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { googleConfig } from './config';
import { AUTH_URL, VERIFY_URL } from './api-config';
import { authenticatedPost } from './utils/apiUtils';
import { clearAllCache } from './services/cacheService';

// Views
import LoginView from './components/LoginView';
import HomeView from './components/HomeView';
import UserView from './components/UserView';
import CharityView from './components/CharityView';
import PledgeView from './components/PledgeView';
import SettingsView from './components/SettingsView';
import Navbar from './components/Navbar';
import LoadingSpinner from './components/common/LoadingSpinner';
import EnvironmentIndicator from './components/common/EnvironmentIndicator';

// Create AuthContext
export const AuthContext = createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifySession = async () => {
      const sessionToken = localStorage.getItem('sessionToken');
      const tokenExpiry = localStorage.getItem('tokenExpiry');
      const storedUserData = localStorage.getItem('userData');
      
      if (sessionToken && tokenExpiry && storedUserData) {
        const expiryTime = parseInt(tokenExpiry);
        const currentTime = Date.now();
        
        // Check if token is still valid (not expired)
        if (currentTime < expiryTime) {
          try {
            const userData = JSON.parse(storedUserData);
            console.log("Token still valid, logging in without backend verification");
            
            setUser({
              ...userData,
              sessionToken
            });
            setLoading(false);
            return;
          } catch (error) {
            console.error("Error parsing stored user data:", error);
            // Fall through to backend verification
          }
        } else {
          console.log("Token expired, removing from storage");
          localStorage.removeItem('sessionToken');
          localStorage.removeItem('tokenExpiry');
          localStorage.removeItem('userData');
        }
      }
      
      // If no valid token or token expired, try backend verification
      if (sessionToken && (!tokenExpiry || !storedUserData)) {
        try {
          console.log("Verifying session token with backend...");
          const response = await authenticatedPost(VERIFY_URL);
          
          // Handle the new nested response format
          if (response && response.user) {
            const userData = response.user;
            console.log("Session verification successful:", userData);
            
            const userState = {
              email: userData.email,
              name: userData.name,
              is_admin: userData.is_admin,
              user_id: userData.id,
              auth_provider: userData.auth_provider,
              created_at: userData.created_at,
              updated_at: userData.updated_at,
              version: userData.version
            };
            
            // Store user data and set new expiry (1 hour from now)
            const newExpiry = Date.now() + (60 * 60 * 1000); // 1 hour
            localStorage.setItem('tokenExpiry', newExpiry.toString());
            localStorage.setItem('userData', JSON.stringify(userState));
            
            setUser({
              ...userState,
              sessionToken
            });
          } else {
            throw new Error('Invalid response format from verification endpoint');
          }
        } catch (error) {
          console.error("Session verification error:", error);
          localStorage.removeItem('sessionToken');
          localStorage.removeItem('tokenExpiry');
          localStorage.removeItem('userData');
          setUser(null);
        }
      } else if (!sessionToken) {
        console.log("No session token found in localStorage");
      }
      setLoading(false);
    };

    verifySession();
  }, []);

  const handleLoginSuccess = async (credentialResponse) => {
    try {
      console.log("Google Login Success:", credentialResponse);
      const googleToken = credentialResponse.credential;
      
      // Send the Google token to our backend for validation
      const response = await fetch(AUTH_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({ google_token: googleToken })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Authentication failed');
      }
      const response_body = await response.json();
      console.log('Complete auth response:', response_body);
      
      const sessionToken = response_body.session_token;
      console.log('Session token received:', sessionToken);
      
      // Validate admin status
      const isAdmin = Boolean(response_body.user?.is_admin);
      console.log('User admin status:', isAdmin);
      
      if (!isAdmin) {
        throw new Error('Access denied. Admin privileges required.');
      }

      // Start every new session from a clean slate so we never inherit data
      // cached against a different user or a different backend environment.
      clearAllCache();

      // Store the session token and expiry for future API calls
      const tokenExpiry = Date.now() + (60 * 60 * 1000); // 1 hour from now
      localStorage.setItem('sessionToken', sessionToken);
      localStorage.setItem('tokenExpiry', tokenExpiry.toString());
      
      // Set user state with the backend response data
      const userState = {
        email: response_body.user.email,
        name: response_body.user.name,
        given_name: response_body.user.given_name,
        picture_url: response_body.user.picture_url,
        is_admin: isAdmin, // Ensure boolean value
        user_id: response_body.user.id,
        auth_provider: response_body.user.auth_provider,
        created_at: response_body.user.created_at,
        updated_at: response_body.user.updated_at
      };
      
      // Store user data for offline validation
      localStorage.setItem('userData', JSON.stringify(userState));
      
      setUser({
        ...userState,
        sessionToken
      });

      // Return a resolved promise to trigger navigation
      return Promise.resolve();
      
    } catch (error) {
      console.error('Authentication error:', error);
      alert(error.message || 'Failed to authenticate. Please try again.');
      localStorage.removeItem('sessionToken');
      setUser(null);
      return Promise.reject(error);
    }
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('userData');
    // Wipe all cached API data so the next session starts clean and never
    // inherits another user's / another backend's data.
    clearAllCache();
    console.log("User logged out");
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gray-100">
        <LoadingSpinner size="lg" />
        <p className="text-gray-600 text-lg mt-4">Verifying session...</p>
        <p className="text-gray-500 text-sm mt-2">Please wait while we check your authentication</p>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={googleConfig.clientId}>
      <AuthContext.Provider value={{ user, setUser, handleLoginSuccess, handleLogout }}>
        <Router>
          <div className="min-h-screen bg-gray-100">
            <EnvironmentIndicator />
            {user && user.is_admin && <Navbar />}
            <main className={user && user.is_admin ? "p-4 md:p-8" : ""}>
              <Routes>
                <Route path="/login" element={<LoginView />} />
                <Route element={<ProtectedRoute user={user} />}>
                  <Route path="/" element={<HomeView />} />
                  <Route path="/users/:userId" element={<UserView />} />
                  <Route path="/charities/:charityId" element={<CharityView />} />
                  <Route path="/pledges/:pledgeId" element={<PledgeView />} />
                  <Route path="/settings" element={<SettingsView />} />
                </Route>
                <Route path="*" element={<Navigate to={user && user.is_admin ? "/" : "/login"} />} />
              </Routes>
            </main>
          </div>
        </Router>
      </AuthContext.Provider>
    </GoogleOAuthProvider>
  );
}

// ProtectedRoute component
const ProtectedRoute = ({ user }) => {
  const location = useLocation();
  console.log("ProtectedRoute check - User state:", {
    userExists: !!user,
    isAdmin: user?.is_admin,
    currentPath: location.pathname
  });
  
  if (!user || !user.is_admin) {
    console.log("ProtectedRoute - Redirecting to login");
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  console.log("ProtectedRoute - Allowing access to protected route");
  return <Outlet />;
};

export default App;